<template>
  <div>
    <div v-if="loaded">
      <TheListHeader :title="t('pages.exports.exportPage.title')" />

      <div v-if="table.rows && table.rows.length > 0" class="px-6 sm:px-12 sm:pb-0">
        <AppTable
          :columns="translatedColumns"
          :rows="table.rows"
          :pagination="table.pagination"
          :sort="table.sort"
          @update:change-page="changePage"
          @update:change-sort="changeSort"
        >
          <template #td-status="{ data }">
            <BaseIcon :name="data.status.icon" :class="`h-5 w-5 text-${data.status.color}-500`" />
          </template>
          <template #td-createdAt="{ data }">
            <div>
              <div class="text-sm font-medium text-foreground">{{ data.createdAt.date }}</div>
              <div v-if="data.createdAt.time" class="text-sm text-muted-foreground">
                {{ data.createdAt.time }}
              </div>
            </div>
          </template>
          <template #td-processedAt="{ data }">
            <div>
              <div class="text-sm font-medium text-foreground">{{ data.processedAt.date }}</div>
              <div v-if="data.processedAt.time" class="text-sm text-muted-foreground">
                {{ data.processedAt.time }}
              </div>
            </div>
          </template>
          <template #td-filename="{ id, data }">
            <button
              v-if="data.filename"
              type="button"
              class="inline-flex items-center gap-2 text-primary underline hover:text-primary/80 disabled:cursor-wait disabled:opacity-60 disabled:no-underline"
              :disabled="downloadingId === id"
              @click="downloadFile(id)"
            >
              <Spinner v-if="downloadingId === id" size="sm" class="shrink-0 text-primary" />
              {{ data.filename }}
            </button>
            <span v-else>-</span>
          </template>
        </AppTable>
      </div>
      <TheEmptyState
        v-else
        module="exports"
        :title="$t(`pages.exports.emptyState.title`)"
        :description="$t(`pages.exports.emptyState.description`)"
      />
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import TheEmptyState from '@/components/layout/TheEmptyState.vue'

export default {
  components: {
    TheLoadingPage,
    TheEmptyState
  },
  setup() {
    const { t } = useI18n()
    const notification = useNotification()

    return {
      t,
      notification
    }
  },
  data() {
    return {
      loaded: false,
      downloadingId: null,
      exports: [],
      pollTimeoutId: null,
      isComponentActive: true,
      table: {
        sort: {
          sortBy: 'createdAt',
          sortOrder: 'DESC'
        },
        pagination: {
          currentPage: 1
        },
        columns: {
          status: {
            id: 'status',
            label: 'pages.exports.exportPage.tables.thead.status',
            sortable: false
          },
          exportType: {
            id: 'exportType',
            label: 'pages.exports.exportPage.tables.thead.exportType',
            sortable: false
          },
          createdAt: {
            id: 'createdAt',
            label: 'pages.exports.exportPage.tables.thead.createdAt'
          },
          processedAt: {
            id: 'processedAt',
            label: 'pages.exports.exportPage.tables.thead.processedAt'
          },
          progress: {
            id: 'progress',
            label: 'pages.exports.exportPage.tables.thead.progress',
            sortable: false
          },
          filename: {
            id: 'filename',
            label: 'pages.exports.exportPage.tables.thead.filename',
            sortable: false
          }
        }
      }
    }
  },
  computed: {
    translatedColumns() {
      return Object.fromEntries(
        Object.entries(this.table.columns).map(([key, col]) => [key, { ...col, label: this.$t(col.label) }])
      )
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.exports.exportPage.title'),
          route: 'ExportList'
        }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  beforeUnmount() {
    this.isComponentActive = false
    this.stopPolling()
  },
  methods: {
    stopPolling() {
      if (this.pollTimeoutId !== null) {
        clearTimeout(this.pollTimeoutId)
        this.pollTimeoutId = null
      }
    },
    changePage(page) {
      this.table.pagination.currentPage = page
      this.fetchData()
    },
    changeSort(sort) {
      this.table.sort = sort
      this.fetchData()
    },
    downloadFile(id) {
      if (this.downloadingId === id) return

      this.downloadingId = id
      this.axios
        .get(`/exports/${id}/download`, {
          responseType: 'blob'
        })
        .then((response) => {
          var fileURL = window.URL.createObjectURL(new Blob([response.data]))
          var fileLink = document.createElement('a')

          const filename = this.exports.filter((row) => row.id === id)[0].filename

          fileLink.href = fileURL
          fileLink.setAttribute('download', filename)
          document.body.appendChild(fileLink)

          fileLink.click()
          this.notification.create('success', {
            text: this.$t('global.download.success'),
            timeout: 2000
          })
        })
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('global.download.error'),
            persist: true
          })
        })
        .finally(() => {
          this.downloadingId = null
        })
    },
    fetchData() {
      this.stopPolling()

      this.axios
        .get('/exports', {
          params: {
            sortBy: this.table.sort.sortBy,
            sortOrder: this.table.sort.sortOrder,
            pageSize: 10,
            page: this.table.pagination.currentPage
          }
        })
        .then((response) => {
          if (!this.isComponentActive) {
            return
          }

          this.exports = response.data.data
          var rows = []
          response.data.data.forEach((row) => {
            let createdAt = {}
            if (row.createdAt === null) {
              createdAt = {
                date: '-',
                time: ''
              }
            } else {
              createdAt = {
                date: new Date(row.createdAt).toLocaleDateString('fr-FR'),
                time: new Date(row.createdAt).toLocaleTimeString('fr-FR')
              }
            }
            let processedAt = {}
            if (row.processedAt === null) {
              processedAt = {
                date: '-',
                time: ''
              }
            } else {
              processedAt = {
                date: new Date(row.processedAt).toLocaleDateString('fr-FR'),
                time: new Date(row.processedAt).toLocaleTimeString('fr-FR')
              }
            }

            let status = ''
            if (row.processedAt === null) {
              status = {
                color: 'yellow',
                icon: 'HeroClockIconOutline'
              }
            } else {
              status = {
                color: 'green',
                icon: 'HeroCheckIconOutline'
              }
            }

            const progress = this.$t(`pages.exports.exportPage.statuses.${row.status}`)

            rows.push({
              id: row.id,
              data: {
                status: status,
                exportType: this.$t(`pages.exports.exportPage.${row.exportType.slug}`),
                createdAt: createdAt,
                processedAt: processedAt,
                progress: progress,
                filename: row.filename
              }
            })
          })
          this.table.rows = rows
          this.table.pagination = {
            currentPage: response.data.meta.current_page,
            lastPage: response.data.meta.last_page,
            total: response.data.meta.total
          }

          if (this.exports.filter((row) => row.processedAt === null).length > 0) {
            this.pollTimeoutId = setTimeout(this.fetchData, 5000)
          }
          this.loaded = true
        })
    }
  }
}
</script>
